import { cx } from "../../lib/cx.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { IconButton } from "../IconButton/IconButton.js";
import { LinkButton } from "../LinkButton/LinkButton.js";
import { useControllable } from "../../lib/useControllable.js";
import { Money } from "../Money/Money.js";
/* empty css                      */
import { useId, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/CategoryBreakdown/CategoryBreakdown.tsx
/**
* Pay History's breakdown: a collapsible card with the total in its header, and earnings
* and deductions by category in two columns, each with its subtotal. Past `maxRows` a
* column shows "Show all". A category in both columns has a dot that says so.
*/
function CategoryBreakdown({ title = "Category breakdown", total, earnings, deductions, maxRows = 8, defaultShowAll = false, collapsed: collapsedProp, defaultCollapsed = false, onCollapsedChange, pairTooltip = (label) => `${label} is in both earnings and deductions`, headingLevel = 2, emptyLabel = "None", className, ...rest }) {
	const id = useId();
	const [collapsed, setCollapsed] = useControllable(collapsedProp, defaultCollapsed, onCollapsedChange);
	const Heading = `h${headingLevel}`;
	const ColumnHeading = `h${headingLevel + 1}`;
	const titleId = `${id}title`;
	const inBoth = new Set(earnings.map((row) => row.label).filter((label) => deductions.some((d) => d.label === label)));
	return /* @__PURE__ */ jsxs("section", {
		...rest,
		"aria-labelledby": titleId,
		className: cx("ds-breakdown", collapsed && "ds-breakdown--collapsed", className),
		children: [/* @__PURE__ */ jsxs("div", {
			className: "ds-breakdown__head",
			children: [/* @__PURE__ */ jsx(Heading, {
				id: titleId,
				className: "ds-breakdown__title",
				children: title
			}), /* @__PURE__ */ jsxs("span", {
				className: "ds-breakdown__head-end",
				children: [/* @__PURE__ */ jsxs("span", {
					className: "ds-breakdown__total",
					children: [/* @__PURE__ */ jsx("span", {
						className: "ds-breakdown__sr",
						children: "Total "
					}), /* @__PURE__ */ jsx(Money, {
						value: total,
						tone: "neutral"
					})]
				}), /* @__PURE__ */ jsx(IconButton, {
					icon: "chevron-down",
					size: "sm",
					variant: "secondary",
					className: "ds-breakdown__toggle",
					"aria-label": `${collapsed ? "Show" : "Hide"} the breakdown`,
					"aria-expanded": !collapsed,
					"aria-controls": `${id}body`,
					onClick: () => setCollapsed(!collapsed)
				})]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			id: `${id}body`,
			className: "ds-breakdown__body",
			hidden: collapsed,
			children: [/* @__PURE__ */ jsx(Column, {
				title: "Earnings",
				rows: earnings,
				tone: "positive",
				maxRows,
				defaultShowAll,
				inBoth,
				pairTooltip,
				emptyLabel,
				Heading: ColumnHeading
			}), /* @__PURE__ */ jsx(Column, {
				title: "Deductions",
				rows: deductions,
				tone: "negative",
				maxRows,
				defaultShowAll,
				inBoth,
				pairTooltip,
				emptyLabel,
				Heading: ColumnHeading
			})]
		})]
	});
}
function sum(rows) {
	return rows.reduce((cents, row) => cents + Math.round(Number(row.amount) * 100), 0) / 100;
}
function Column({ title, rows, tone, maxRows, defaultShowAll, inBoth, pairTooltip, emptyLabel, Heading }) {
	const id = useId();
	const [showAll, setShowAll] = useState(defaultShowAll);
	const shown = showAll ? rows : rows.slice(0, maxRows);
	const subtotal = sum(rows);
	return /* @__PURE__ */ jsxs("div", {
		role: "group",
		className: "ds-breakdown__column",
		"aria-labelledby": `${id}title`,
		children: [
			/* @__PURE__ */ jsxs(Heading, {
				id: `${id}title`,
				className: "ds-breakdown__column-title",
				children: [/* @__PURE__ */ jsx("span", { children: title }), /* @__PURE__ */ jsx(Money, {
					value: subtotal,
					tone: subtotal === 0 ? "neutral" : tone,
					signed: tone === "positive",
					className: "ds-breakdown__subtotal"
				})]
			}),
			rows.length === 0 ? /* @__PURE__ */ jsx("p", {
				className: "ds-breakdown__empty",
				children: emptyLabel
			}) : /* @__PURE__ */ jsx("ul", {
				id: `${id}rows`,
				className: "ds-breakdown__rows",
				children: shown.map((row) => {
					const paired = inBoth.has(row.label);
					const tip = pairTooltip(row.label);
					return /* @__PURE__ */ jsxs("li", {
						className: "ds-breakdown__row",
						children: [/* @__PURE__ */ jsxs("span", {
							className: "ds-breakdown__label",
							children: [row.label, paired && /* @__PURE__ */ jsx(Tooltip, {
								content: tip,
								describes: false,
								children: /* @__PURE__ */ jsx("span", {
									className: "ds-breakdown__pair",
									role: "img",
									"aria-label": tip,
									tabIndex: 0
								})
							})]
						}), /* @__PURE__ */ jsx(Money, {
							value: row.amount,
							tone,
							signed: tone === "positive",
							className: "ds-breakdown__amount"
						})]
					}, row.label);
				})
			}),
			rows.length > maxRows && /* @__PURE__ */ jsx(LinkButton, {
				className: "ds-breakdown__show-all",
				"aria-expanded": showAll,
				"aria-controls": `${id}rows`,
				onClick: () => setShowAll(!showAll),
				children: showAll ? "Show less" : `Show all ${rows.length}`
			})
		]
	});
}
//#endregion
export { CategoryBreakdown };

//# sourceMappingURL=CategoryBreakdown.js.map